import Config from '@config';
import { normalizeInfiniteQueryData } from '@gasbi/shared/common/features/utils';
import { createApi } from '@reduxjs/toolkit/query/react';
import { useSelector } from 'react-redux';

import { CACHE_EXPIRATION_TIME } from '@/features/constants';
import { FiltersGrid, Pagination } from '@/features/types';
import { cachedItemByIdSelector, infiniteQueryOptions } from '@/features/utils';
import axiosBaseQuery from '@/utils/axiosBaseQuery';

import {
  GasThreadOperationalIncomeReadModel,
  OperatingIncome,
  OperatingIncomeCanUpdateConsumptionQueryParams,
  OperatingIncomeGetItemQueryParams,
  OperatingIncomeGrid,
  OperatingIncomeMeteringPointsFilterGrid,
  OperatingIncomeMeteringPointsGrid,
  OperatingIncomeTotals,
  OperatingIncomeTotalsFilterGird,
  OperatingIncomeUpdateConsumptionPayload,
  OperatingIncomeUpdatePressurePayload,
} from './types';

export const operatingIncomesApi = createApi({
  reducerPath: 'operatingIncomesApi',
  baseQuery: axiosBaseQuery({
    baseUrl: `${Config.VITE_API_URL}/gas-distribution-station-operational-incomes`,
  }),
  tagTypes: [
    'OperatingIncomes',
    'OperatingIncome',
    'OperatingIncomeTotals',
    'OperatingIncomeMeteringPoints',
    'OperatingIncomeMeteringPointsTotals',
  ],
  keepUnusedDataFor: CACHE_EXPIRATION_TIME,
  endpoints: builder => ({
    getOperatingIncomes: builder.infiniteQuery<
      OperatingIncomeGrid[],
      FiltersGrid | void,
      Pagination
    >({
      infiniteQueryOptions,
      query: ({ queryArg, pageParam }) => {
        return {
          url: `/gas-thread/query`,
          params: { ...queryArg, ...pageParam },
        };
      },
      providesTags: () => ['OperatingIncomes', 'OperatingIncome'],
    }),
    getOperatingIncomeTotals: builder.query<
      OperatingIncomeTotals,
      OperatingIncomeTotalsFilterGird
    >({
      query: params => {
        return {
          url: '/gas-thread/query/totals',
          params,
        };
      },
      providesTags: () => ['OperatingIncomeTotals'],
    }),
    getOperatingIncomeById: builder.query<
      GasThreadOperationalIncomeReadModel,
      OperatingIncomeGetItemQueryParams
    >({
      query: ({ gasThreadId, date }) => ({
        url: `/gas-thread`,
        params: { gasThreadId, date },
      }),
      providesTags: result => [
        {
          type: 'OperatingIncome',
          id: `${result?.gasThreadId}-${result?.date}`,
        },
      ],
    }),
    getOperatingIncomeMeteringPoints: builder.query<
      OperatingIncomeMeteringPointsGrid[],
      OperatingIncomeMeteringPointsFilterGrid
    >({
      query: params => {
        return {
          url: '/metering-points/query',
          params,
        };
      },
      providesTags: () => ['OperatingIncomeMeteringPoints'],
      transformResponse: (response: OperatingIncomeMeteringPointsGrid[]) => {
        // this id is for DataGrid only
        return response?.map(item => ({ ...item, id: item.meteringPointId }));
      },
    }),
    getOperatingIncomeMeteringPointsTotals: builder.query<
      OperatingIncomeTotals,
      OperatingIncomeTotalsFilterGird
    >({
      query: params => {
        return {
          url: '/metering-points/query/totals',
          params,
        };
      },
      providesTags: () => ['OperatingIncomeMeteringPointsTotals'],
    }),
    updatePressureIn: builder.mutation<
      OperatingIncome,
      OperatingIncomeUpdatePressurePayload
    >({
      query: data => ({
        url: `/gas-thread/pressure-in`,
        method: 'PUT',
        data,
      }),
      invalidatesTags: result => [
        {
          type: 'OperatingIncome',
          id: `${result?.gasThreadId}-${result?.date}`,
        },
      ],
    }),
    updatePressureOut: builder.mutation<
      OperatingIncome,
      OperatingIncomeUpdatePressurePayload
    >({
      query: data => ({
        url: `/gas-thread/pressure-out`,
        method: 'PUT',
        data,
      }),
      invalidatesTags: result => [
        {
          type: 'OperatingIncome',
          id: `${result?.gasThreadId}-${result?.date}`,
        },
      ],
    }),
    gasThreadValid: builder.query<boolean, OperatingIncomeGetItemQueryParams>({
      query: ({ gasThreadId, date }) => ({
        url: `/gas-thread/valid?gasThreadId=${gasThreadId}&date=${date}`,
      }),
    }),
    canUpdateConsumption: builder.query<
      boolean,
      OperatingIncomeCanUpdateConsumptionQueryParams
    >({
      query: ({ meteringPointId, date }) => ({
        url: `/metering-point/consumption/can-update?meteringPointId=${meteringPointId}&date=${date}`,
      }),
    }),
    updateConsumption: builder.mutation<
      OperatingIncome,
      OperatingIncomeUpdateConsumptionPayload
    >({
      query: data => ({
        url: `/metering-point/consumption`,
        method: 'PUT',
        data,
      }),
      invalidatesTags: (_, __, params) => [
        'OperatingIncomes',
        {
          type: 'OperatingIncome',
          id: `${params.gasThreadId}-${params.date}`,
        },
      ],
    }),
  }),
});

export const {
  useGetOperatingIncomeByIdQuery,
  useLazyGetOperatingIncomeByIdQuery,
  useUpdatePressureInMutation,
  useUpdatePressureOutMutation,
  useGasThreadValidQuery,
  useCanUpdateConsumptionQuery,
  useUpdateConsumptionMutation,
  useGetOperatingIncomeTotalsQuery,
  useGetOperatingIncomeMeteringPointsQuery,
  useGetOperatingIncomeMeteringPointsTotalsQuery,
} = operatingIncomesApi;

export const useGetOperatingIncomesIInfiniteQuery = normalizeInfiniteQueryData(
  operatingIncomesApi.useGetOperatingIncomesInfiniteQuery,
);

const getCachedOperatingIncomeByIdSelector = cachedItemByIdSelector(
  'operatingIncomesApi',
  'getOperatingIncomesI',
  'gasThreadId',
);

export const useGetCachedOperatingIncomeByIdSelector = id =>
  useSelector(getCachedOperatingIncomeByIdSelector(id));
